
import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import CourseNotificationDialog from "@/components/courses/CourseNotificationDialog";
import PaymentStatusDialog from "@/components/payment/PaymentStatusDialog";
import { OptimizedCourseCard } from "@/components/courses/OptimizedCourseCard";

const isDevelopment = import.meta.env.DEV;
const logger = {
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },
  error: (...args: any[]) => {
    if (isDevelopment) {
      console.error(...args);
    }
  }
};

interface Course {
  id: string;
  title: string;
  description: string;
  level: string;
  duration: string;
  price: string;
  status: string;
  image_url: string | null;
  text_direction: string;  // Changed from "ltr" | "rtl" to string to match database
  enrolled?: boolean;
}

const Courses = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notificationCourse, setNotificationCourse] = useState<Course | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState<{
    isOpen: boolean;
    status: "success" | "failed" | "pending" | null;
    message?: string;
  }>({
    isOpen: false,
    status: null
  });
  const { toast } = useToast();
  const { user, hasRole, checkEnrollment } = useAuth();
  const navigate = useNavigate();
  
  const canCreateCourse = user && (hasRole("admin") || hasRole("super_admin"));
  
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('courses')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) {
          throw error;
        }
        
        if (user) {
          const coursesWithEnrollmentStatus = await Promise.all(
            (data || []).map(async (course) => {
              const isEnrolled = await checkEnrollment(course.id);
              return { ...course, enrolled: isEnrolled } as Course;
            })
          );
          setCourses(coursesWithEnrollmentStatus);
        } else {
          setCourses(data as Course[] || []);
        }
      } catch (error: any) {
        logger.error('Error fetching courses:', error.message);
        toast({
          title: "Failed to load courses",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourses();
    
    const status = searchParams.get('status');
    const message = searchParams.get('message');
    const courseId = searchParams.get('courseId');
    
    if (status === 'success' || status === 'failed' || status === 'pending') {
      setPaymentStatus({
        isOpen: true,
        status: status as "success" | "failed" | "pending",
        message: message || undefined
      });
      
      setSearchParams({});
      
      if (status === 'success' && courseId) {
        setTimeout(() => {
          navigate(`/courses/${courseId}/lessons`);
        }, 3000);
      }
    }
  }, [toast, searchParams, setSearchParams, navigate, user, checkEnrollment]);

  const handleGetNotified = (course: Course) => {
    setNotificationCourse(course);
  };
  
  const closePaymentDialog = () => {
    setPaymentStatus(prev => ({ ...prev, isOpen: false }));
  };

  const truncateDescription = (html: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    const maxChars = 240;
    
    if (textContent.length <= maxChars) {
      return html;
    }
    
    const truncated = textContent.substring(0, maxChars);
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastSpace > maxChars * 0.8) {
      return truncated.substring(0, lastSpace) + '...';
    }
    
    return truncated + '...';
  };

  return (
    <Layout 
      title="Courses - Freelance With Andy" 
      description="Level up your skills with hands-on, practical courses designed for the modern digital professional by Andrew Nady."
      keywords="courses, online learning, andrew, andy, product, projects, digital, freelance"
      path="/courses"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold">
            <span className="text-[#34BF49]">Courses</span>
          </h1>
          {canCreateCourse && (
            <Link to="/courses/create">
              <Button className="flex items-center gap-2">
                <PlusCircle size={18} />
                Create Course
              </Button>
            </Link>
          )}
        </div>
        <p className="text-xl text-gray-600 mb-10">
          Level up your skills with hands-on, practical courses designed for the modern digital professional.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-pulse">Loading courses...</div>
          </div>
        ) : courses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course) => (
              <OptimizedCourseCard 
                key={course.id}
                id={course.id}
                title={course.title}
                level={course.level}
                price={course.price}
                status={course.status}
                image_url={course.image_url}
                enrolled={course.enrolled}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-gray-600 mb-6">No courses found.</p>
            {canCreateCourse && (
              <Link to="/courses/create">
                <Button className="flex items-center gap-2 mx-auto">
                  <PlusCircle size={18} />
                  Create First Course
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {notificationCourse && (
        <CourseNotificationDialog 
          courseId={notificationCourse.id} 
          courseName={notificationCourse.title} 
          isOpen={!!notificationCourse} 
          onClose={() => setNotificationCourse(null)} 
        />
      )}
      
      <PaymentStatusDialog
        isOpen={paymentStatus.isOpen}
        onClose={closePaymentDialog}
        status={paymentStatus.status}
        message={paymentStatus.message}
      />
    </Layout>
  );
};

export default Courses;
